import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { usePlan } from '../../api/hooks/usePlans.ts';
import { recordPlanEvent } from '../../api/hooks/useSharing.ts';
import PlanDocument, { PAGE_TITLES } from '../../components/print/PlanDocument.tsx';

export default function PlanViewer() {
  const { planId } = useParams<{ planId: string }>();
  const plan = usePlan(planId);
  const [page, setPage] = useState<number | undefined>(1);

  // per-section reading stats for shared viewers (the server ignores owner events)
  const doc = plan.data;
  const isViewer = doc?.role === 'viewer';
  const slugKey =
    doc && page !== undefined
      ? doc.sections
          .filter((s) => s.section.page === page)
          .map((s) => s.section.slug)
          .join(',')
      : '';
  useEffect(() => {
    if (!planId || !isViewer || !slugKey) return;
    for (const slug of slugKey.split(',')) recordPlanEvent(planId, slug);
  }, [planId, isViewer, slugKey]);

  if (plan.isPending) {
    return <main className="p-8 text-center text-sm text-gray-400">Loading…</main>;
  }
  if (plan.isError) {
    return (
      <main className="p-8 text-center text-sm text-red-600">
        Could not load this plan: {plan.error.message}
      </main>
    );
  }

  if (!doc) return null;
  const pages = [...new Set(doc.sections.map((s) => s.section.page))].sort((a, b) => a - b);

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="sticky top-0 z-10 border-b border-gray-200 bg-white px-6 py-3 print:hidden">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            {doc.role === 'owner' && (
              <Link to={`/plans/${doc.plan.id}/edit`} className="text-sm text-blue-600 hover:underline">
                ← Edit
              </Link>
            )}
            <span className="text-sm font-semibold text-gray-700">{doc.plan.title}</span>
          </div>
          <div className="flex items-center gap-2">
            {pages.map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                title={PAGE_TITLES[p]}
                className={`rounded-full px-3 py-1 text-sm font-medium ${
                  p === page ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage(undefined)}
              className={`rounded-full px-3 py-1 text-sm font-medium ${
                page === undefined ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => window.print()}
              className="ml-2 rounded-md border border-gray-300 px-3 py-1 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Print / PDF
            </button>
          </div>
        </div>
      </nav>
      <div className="py-8 print:py-0">
        <div className="mx-auto max-w-3xl shadow-sm print:max-w-none print:shadow-none">
          <PlanDocument doc={doc} onlyPage={page} />
        </div>
      </div>
    </div>
  );
}
